import { ActivityIcon, BarChart3Icon, BriefcaseIcon, FileTextIcon, ChevronsUpDownIcon, LifeBuoyIcon, LogOutIcon, MenuIcon, PanelLeftCloseIcon, PanelLeftOpenIcon, PlusIcon, SearchIcon, SettingsIcon, UserIcon, UsersIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router';
import { CommandPaletteProvider, useCommandPalette } from '@/components/CommandPalette';
import { JobMark } from '@/components/JobMark';
import { MobileTabBar } from '@/components/MobileTabBar';
import { RailOutlet } from '@/components/RailOutlet';
import { SidebarSection } from '@/components/SidebarSection';
import { SessionWatch } from '@/components/SessionWatch';
import { ReceiptIcon } from 'lucide-react';
import { Link as BillingLink } from 'react-router';
import { Logo } from '@/components/Logo';
import { ThemeMenuItems } from '@/components/ThemeToggle';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth, useMe } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { STATUS_PAGE_URL } from '@/lib/serviceStatus';
import { SIDEBAR_MAX, SIDEBAR_MIN, useSidebar } from '@/lib/sidebar';
import { cn, initials } from '@/lib/utils';

const isMac = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform);

/* ------------------------------------------------------------------ sidebar */

/**
 * Sidebar link. The active state is computed with `useMatch` and the class list passed as a plain
 * string: in the collapsed rail the link sits inside a tooltip trigger (`asChild`), and Radix merges
 * `className` by joining strings, which would turn a NavLink className *function* into its source
 * text and leave the link unstyled.
 */
function NavItem({ to, icon, label, end, collapsed, mark }: { to: string; icon?: ReactNode; label: string; end?: boolean; collapsed: boolean; mark?: ReactNode }) {
  const active = useMatch({ path: to, end: end ?? false }) !== null;
  const link = (
    <NavLink
      to={to}
      end={end}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2.5 border-l-2 py-2 text-sm transition-colors',
        collapsed ? 'justify-center px-0' : 'px-3',
        active ? 'border-sidebar-primary bg-sidebar-accent text-sidebar-accent-foreground font-medium' : 'border-transparent text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
      )}
    >
      {icon ?? mark}
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );
  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function SidebarContent({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const me = useMe();
  const { branding } = useBranding();
  const sidebar = useSidebar();
  const activeJobs = me.jobs.filter((j) => j.status === 'active');
  return (
    <div className="flex h-full flex-col" onClick={onNavigate}>
      <div className={cn('flex h-14 shrink-0 items-center border-b', collapsed ? 'justify-center px-0' : 'px-4')}>
        <Link to="/jobs" className="flex min-w-0 items-center gap-2" aria-label={`${branding.appName} home`}>
          {collapsed ? <span className="bg-primary text-primary-foreground grid size-7 place-items-center text-xs font-semibold">{branding.appName.slice(0, 1).toUpperCase()}</span> : <Logo />}
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto py-3" aria-label="Sections">
        <SidebarSection
          id="jobs"
          label="Jobs"
          collapsed={collapsed}
          action={
            me.permissions.createJobs ? (
              <Link to="/jobs?new=1" className="text-muted-foreground hover:text-foreground grid size-7 place-items-center" aria-label="New job">
                <PlusIcon className="size-3.5" />
              </Link>
            ) : undefined
          }
        >
          <NavItem to="/jobs" end icon={<BriefcaseIcon className="size-4 shrink-0" />} label="All jobs" collapsed={collapsed} />
          {activeJobs.map((j) => (
            <NavItem key={j.id} to={`/jobs/${j.id}`} label={j.name} mark={<JobMark job={j} size="xs" />} collapsed={collapsed} />
          ))}
          {activeJobs.length === 0 && !collapsed && <p className="text-muted-foreground px-4 py-2 pl-9 text-xs">No jobs yet</p>}
        </SidebarSection>

        {me.permissions.manageUsers && (
          <SidebarSection id="organization" label="Organization" collapsed={collapsed} divider className="mt-5">
            <NavItem to="/org/users" icon={<UsersIcon className="size-4 shrink-0" />} label="Users" collapsed={collapsed} />
            {me.tenant.features.reports && <NavItem to="/org/reports" icon={<BarChart3Icon className="size-4 shrink-0" />} label="Reports" collapsed={collapsed} />}
            {me.tenant.features.helpRequests && <NavItem to="/org/support" icon={<LifeBuoyIcon className="size-4 shrink-0" />} label="Help requests" collapsed={collapsed} />}
            {me.tenant.features.templates && <NavItem to="/org/templates" icon={<FileTextIcon className="size-4 shrink-0" />} label="Templates" collapsed={collapsed} />}
            {me.tenant.features.billing && <NavItem to="/org/billing" icon={<ReceiptIcon className="size-4 shrink-0" />} label="Billing" collapsed={collapsed} />}
            <NavItem to="/org/settings" icon={<SettingsIcon className="size-4 shrink-0" />} label="Settings" collapsed={collapsed} />
          </SidebarSection>
        )}
      </nav>

      <div className="safe-bottom shrink-0 border-t">
        <div className={cn('text-muted-foreground flex items-center gap-2 px-3 py-2 text-xs', collapsed && 'justify-center px-0')}>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-foreground truncate font-medium">{branding.tenantName}</div>
              {me.tenant.pilotEndsOn && <div className="truncate">Pilot through {me.tenant.pilotEndsOn}</div>}
            </div>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  sidebar.toggle();
                }}
                className="hover:text-foreground hidden size-7 shrink-0 place-items-center transition-colors md:grid"
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? <PanelLeftOpenIcon className="size-4" /> : <PanelLeftCloseIcon className="size-4" />}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">{collapsed ? 'Expand sidebar' : 'Collapse sidebar'} · [</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

/** Drag to resize; double-click resets; arrow keys nudge; dragging past the minimum collapses to the rail. */
function ResizeHandle() {
  const sidebar = useSidebar();
  const dragging = useRef(false);
  const [active, setActive] = useState(false);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragging.current = true;
      setActive(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      const startX = e.clientX;
      const startW = sidebar.collapsed ? SIDEBAR_MIN : sidebar.width;
      const move = (ev: PointerEvent) => {
        if (!dragging.current) return;
        const w = startW + (ev.clientX - startX);
        if (w < SIDEBAR_MIN - 48) sidebar.setCollapsed(true);
        else sidebar.setWidth(w);
      };
      const up = () => {
        dragging.current = false;
        setActive(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    },
    [sidebar],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-valuemin={SIDEBAR_MIN}
      aria-valuemax={SIDEBAR_MAX}
      aria-valuenow={sidebar.effectiveWidth}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onDoubleClick={sidebar.reset}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') sidebar.setWidth((sidebar.collapsed ? SIDEBAR_MIN : sidebar.width) - 16);
        else if (e.key === 'ArrowRight') sidebar.setWidth((sidebar.collapsed ? SIDEBAR_MIN : sidebar.width) + 16);
        else if (e.key === 'Enter') sidebar.toggle();
        else if (e.key === 'Home') sidebar.reset();
      }}
      className="group absolute inset-y-0 -right-1.5 z-40 hidden w-3 cursor-col-resize outline-none md:block"
    >
      <div className={cn('absolute inset-y-0 left-1 w-px transition-colors group-hover:bg-primary group-focus-visible:bg-primary', active ? 'bg-primary' : 'bg-transparent')} />
    </div>
  );
}

/* ------------------------------------------------------------------ header */

function UserMenu() {
  const me = useMe();
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-9 gap-2 px-2">
          <Avatar className="size-7">
            <AvatarFallback>{initials(me.user.fullName)}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-[160px] truncate text-sm md:inline">{me.user.fullName}</span>
          <ChevronsUpDownIcon className="text-muted-foreground hidden size-3.5 md:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="truncate text-sm font-medium">{me.user.fullName}</div>
          <div className="text-muted-foreground truncate text-xs">{me.user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/account')}>
          <UserIcon /> Account
        </DropdownMenuItem>
        {me.tenant.features.helpRequests && (
          <DropdownMenuItem onClick={() => navigate('/support')}>
            <LifeBuoyIcon /> My help requests
          </DropdownMenuItem>
        )}
        {me.tenant.features.helpRequests && (
          <DropdownMenuItem onClick={() => navigate('/help')}>
            <LifeBuoyIcon /> Help
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => window.open(STATUS_PAGE_URL, '_blank', 'noreferrer')}>
          <ActivityIcon /> System status
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">Theme</DropdownMenuLabel>
        <ThemeMenuItems />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            void logout().then(() => navigate('/login'));
          }}
        >
          <LogOutIcon /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SearchButton() {
  const { setOpen } = useCommandPalette();
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-muted-foreground hover:border-foreground/30 hover:text-foreground bg-card hidden h-9 items-center gap-3 border px-3 text-sm transition-colors sm:inline-flex sm:w-52 lg:w-64"
        aria-label="Open quick switcher"
      >
        <SearchIcon className="size-4" />
        <span className="flex-1 text-left">Jump to…</span>
        <kbd className="bg-muted text-muted-foreground border px-1 font-mono text-[10px]">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
      </button>
      <Button variant="ghost" size="icon" className="sm:hidden" onClick={() => setOpen(true)} aria-label="Open quick switcher">
        <SearchIcon />
      </Button>
    </>
  );
}

function titleCase(s: string): string {
  return s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function useCrumbs(pathname: string): Array<{ label: string; to?: string }> {
  const me = useMe();
  const parts = pathname.split('/').filter(Boolean);
  const crumbs: Array<{ label: string; to?: string }> = [];
  if (parts[0] === 'jobs') {
    crumbs.push({ label: 'Jobs', to: '/jobs' });
    if (parts[1]) {
      const job = me.jobs.find((j) => j.id === parts[1]);
      crumbs.push({ label: job?.name ?? 'Job', to: `/jobs/${parts[1]}` });
      if (parts[2]) crumbs.push({ label: titleCase(parts[2]) });
    }
  } else if (parts[0] === 'org') {
    crumbs.push({ label: 'Organization' });
    if (parts[1]) crumbs.push({ label: titleCase(parts[1]) });
  } else if (parts[0]) {
    crumbs.push({ label: titleCase(parts[0]) });
  }
  return crumbs;
}

function Breadcrumbs({ pathname }: { pathname: string }) {
  const crumbs = useCrumbs(pathname);
  const last = crumbs[crumbs.length - 1];
  const parent = crumbs.length > 1 ? crumbs[crumbs.length - 2] : undefined;
  return (
    <>
      {/* Small screens: the current place, with one level of context. */}
      <div className="flex min-w-0 items-baseline gap-1.5 text-sm md:hidden">
        {parent ? <span className="text-muted-foreground hidden truncate xs:inline">{parent.label} /</span> : null}
        <span className="text-foreground truncate font-medium">{last?.label ?? ''}</span>
      </div>
      <nav aria-label="Breadcrumb" className="text-muted-foreground hidden min-w-0 items-center gap-1.5 text-sm md:flex">
        {crumbs.map((c, i) => (
          <span key={i} className="flex min-w-0 items-center gap-1.5">
            {i > 0 && <span aria-hidden>/</span>}
            {c.to && i < crumbs.length - 1 ? (
              <Link to={c.to} className="hover:text-foreground truncate">
                {c.label}
              </Link>
            ) : (
              <span className={cn('truncate', i === crumbs.length - 1 && 'text-foreground font-medium')}>{c.label}</span>
            )}
          </span>
        ))}
      </nav>
    </>
  );
}

/* ------------------------------------------------------------------ shell */

function Shell() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const sidebar = useSidebar();

  useEffect(() => setOpen(false), [location.pathname]);

  // `[` toggles the sidebar when not typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.key === '[' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        sidebar.toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebar]);

  return (
    <div className="flex min-h-dvh">
      <aside className="relative hidden shrink-0 transition-[width] duration-200 ease-out md:block" style={{ width: sidebar.effectiveWidth }} aria-label="Primary">
        <div className="bg-sidebar text-sidebar-foreground border-sidebar-border sticky top-0 h-dvh border-r">
          <SidebarContent collapsed={sidebar.collapsed} />
        </div>
        <ResizeHandle />
      </aside>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="bg-sidebar text-sidebar-foreground w-[18rem] p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarContent collapsed={false} onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="bg-background/95 sticky top-0 z-30 border-b px-4 backdrop-blur md:px-8">
          <div className="mx-auto flex h-14 w-full max-w-7xl items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <Button variant="ghost" size="icon" className="-ml-2 md:hidden" onClick={() => setOpen(true)} aria-label="Open navigation">
                <MenuIcon />
              </Button>
              <Breadcrumbs pathname={location.pathname} />
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <SearchButton />
              <UserMenu />
            </div>
          </div>
        </header>
        <BillingBanner />
        <main className="flex-1 px-4 py-6 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:px-8 md:py-8">
          <div className="mx-auto w-full max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Phones get an app-style tab bar instead of the sidebar. */}
      <MobileTabBar />

      {/* Right rail: pinned to the right edge like the sidebar, filled by the current page (see components/RailOutlet). */}
      <RailOutlet />
    </div>
  );
}

export function AppShell() {
  return (
    <CommandPaletteProvider>
      <Shell />
      <SessionWatch />
    </CommandPaletteProvider>
  );
}

/** Overdue / paused notice for everyone in the organization; Super Users get a link to settle it. */
function BillingBanner() {
  const { me, isOrgAdmin } = useAuth();
  const b = me?.billing;
  if (!b || b.serviceState === 'active') return null;
  const suspended = b.serviceState === 'suspended';
  return (
    <div role="alert" className={suspended ? 'border-destructive/40 bg-destructive/5 text-destructive border-b px-4 py-2 text-sm md:px-8' : 'border-warning/60 bg-warning/10 border-b px-4 py-2 text-sm md:px-8'}>
      <span className="font-medium">{suspended ? 'Sending texts is paused.' : 'Overdue balance.'}</span>{' '}
      <span className={suspended ? 'text-destructive/90' : 'text-muted-foreground'}>{b.message}</span>
      {isOrgAdmin ? (
        <>
          {' '}
          <BillingLink to="/org/billing" className="font-medium underline underline-offset-4">
            Open billing
          </BillingLink>
        </>
      ) : null}
    </div>
  );
}
